import type { PasskeyCreationOptionsJSON } from "./api.ts";

/**
 * パスキーの登録（WebAuthn）。My Account API が返す JSON の登録オプションをブラウザの形に直し、
 * 作った認証情報を JSON（バイナリは base64url）にして返す
 */

export function base64UrlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function isPasskeySupported(): boolean {
  return typeof window.PublicKeyCredential === "function" && navigator.credentials !== undefined;
}

export function toCreationOptions(
  json: PasskeyCreationOptionsJSON
): PublicKeyCredentialCreationOptions {
  return {
    challenge: base64UrlToBuffer(json.challenge),
    rp: json.rp,
    user: { ...json.user, id: base64UrlToBuffer(json.user.id) },
    pubKeyCredParams: json.pubKeyCredParams,
    ...(json.authenticatorSelection ? { authenticatorSelection: json.authenticatorSelection } : {}),
    ...(json.timeout ? { timeout: json.timeout } : {}),
    ...(json.excludeCredentials
      ? {
          excludeCredentials: json.excludeCredentials.map((c) => ({
            type: c.type,
            id: base64UrlToBuffer(c.id),
            ...(c.transports ? { transports: c.transports as AuthenticatorTransport[] } : {}),
          })),
        }
      : {}),
  };
}

export type RegistrationResponseJSON = {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment: string | null;
  response: { clientDataJSON: string; attestationObject: string; transports?: string[] };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
};

export function credentialToJSON(credential: PublicKeyCredential): RegistrationResponseJSON {
  const response = credential.response as AuthenticatorAttestationResponse;
  const transports =
    typeof response.getTransports === "function" ? response.getTransports() : undefined;
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? null,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      ...(transports ? { transports } : {}),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

/** 登録オプションからパスキーを作る。ユーザーが中止すると DOMException（NotAllowedError） */
export async function createPasskey(
  options: PasskeyCreationOptionsJSON
): Promise<RegistrationResponseJSON> {
  const credential = (await navigator.credentials.create({
    publicKey: toCreationOptions(options),
  })) as PublicKeyCredential | null;
  if (credential === null) throw new DOMException("No credential", "NotAllowedError");
  return credentialToJSON(credential);
}
